import { useEffect, useMemo, useState } from "react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { useAudit } from "../../../../contexts/auditContext";
import { useHookCache } from "../../../../lib/hookCache";
import { Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { AuditSession } from "../../../../models/audit/auditSession";
import { SampleUploadModal } from "./sampleUpload/SampleUploadModal";
import { PullSamplesFromOptroModal } from "./optroSamples/PullSamplesFromOptroModal";
import { useAuth } from "../../../../contexts/authContext";
import { hasPermission } from "../../../../lib/permissions";
import { permissionDeniedTooltip } from "../../../admin/roles/rolePermissionGroups";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";

export const AuditUploadAdditionalSamples = ({
  auditSession,
}: {
  auditSession: AuditSession;
}) => {
  const { createSourceRow, controlSession } = useAudit();
  const { invalidate } = useHookCache();
  const { workspaceMember } = useAuth();
  const canRunTest = hasPermission(workspaceMember, "run_test");
  const [manualOpen, setManualOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [optroModalOpen, setOptroModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // Only show the Optro pull when the control is Optro-linked (matches the
  // gating in AuditUploadFile.tsx).
  const hasOptro = Boolean(controlSession?.control?.externalRef);

  // Display attributes pulled from the current procedure
  const displayAttributes = useMemo(
    () => auditSession?.procedure?.attributes ?? [],
    [auditSession?.procedure?.attributes],
  );

  const [attributeValues, setAttributeValues] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (!manualOpen) return;
    const init: Record<string, string> = {};
    for (const attr of displayAttributes) {
      init[attr.name] = "";
    }
    setAttributeValues(init);
  }, [manualOpen, displayAttributes]);

  const handleUploadSuccess = () => {
    // ⚠️ REVIEWERS: this handler is SHARED by the CSV/Excel upload AND the Optro
    // pull — this change affects BOTH flows, not just Optro.
    // Refresh the envelope too (not just source-rows): a first upload creates new
    // procedure attributes, and the sample detail table reads its column headers +
    // order from `procedure.attributeLookup` on the envelope. Without this the
    // headers render blank / mis-ordered until a full page reload (pre-existing
    // bug, surfaced by the Optro feature).
    void invalidate(["procedure-session", auditSession.id]);
    void invalidate(["procedure-session", auditSession.id, "source-rows"]);
    if (controlSession) {
      void invalidate(["control-session", controlSession.id]);
    }
  };

  const handleCreateManualSample = async () => {
    const attributes = displayAttributes.map((a) => a.name);
    const data: Record<string, string | number | null> = {};
    for (const name of attributes) {
      const raw = attributeValues[name] ?? "";
      if (raw.trim() === "") data[name] = null;
      else if (!Number.isNaN(Number(raw))) data[name] = Number(raw);
      else data[name] = raw;
    }

    try {
      setCreating(true);
      await createSourceRow({ auditSession, attributes, data });
      setManualOpen(false);
    } catch {
      // errors toasted in context
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="w-fit">
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <DropdownMenuTrigger asChild>
                <Button
                  disabled={
                    !canRunTest ||
                    auditSession?.sourceRowsState === "processing"
                  }
                  variant="outline"
                  loading={auditSession?.sourceRowsState === "processing"}
                  size="default"
                >
                  <Plus size={16} className="mr-2" />
                  Add Samples
                </Button>
              </DropdownMenuTrigger>
            </span>
          </TooltipTrigger>
          {!canRunTest && (
            <TooltipContent>
              {permissionDeniedTooltip("run_test")}
            </TooltipContent>
          )}
        </Tooltip>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => setUploadModalOpen(true)}>
            Add samples from CSV/Excel
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setManualOpen(true)}>
            Add sample manually
          </DropdownMenuItem>
          {hasOptro && (
            <DropdownMenuItem onClick={() => setOptroModalOpen(true)}>
              Pull samples from Optro
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <SampleUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        auditSession={auditSession}
        onSuccess={handleUploadSuccess}
      />

      {hasOptro && controlSession && (
        <PullSamplesFromOptroModal
          open={optroModalOpen}
          onOpenChange={setOptroModalOpen}
          auditSession={auditSession}
          controlSession={controlSession}
          onSuccess={handleUploadSuccess}
        />
      )}

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a sample</DialogTitle>
            <DialogDescription>
              Fill in values for the procedure's attributes
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-3">
            {displayAttributes.length === 0 ? (
              <p className="text-sm text-stone-500">No attributes defined.</p>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {displayAttributes.map((attr) => (
                  <div key={attr.id} className="flex flex-col gap-1">
                    <Label htmlFor={`attr-${attr.id}`}>{attr.name}</Label>
                    <Input
                      id={`attr-${attr.id}`}
                      value={attributeValues[attr.name] ?? ""}
                      onChange={(e) =>
                        setAttributeValues((prev) => ({
                          ...prev,
                          [attr.name]: e.target.value,
                        }))
                      }
                      disabled={
                        creating ||
                        auditSession?.sourceRowsState === "processing"
                      }
                      placeholder={attr.description ?? ""}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              onClick={handleCreateManualSample}
              disabled={
                creating || auditSession?.sourceRowsState === "processing"
              }
              loading={
                creating || auditSession?.sourceRowsState === "processing"
              }
            >
              Create Sample
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
